import React, { useState, useEffect, useRef } from "react"
import QRCode from "qrcode.react"
import { saveQRCode, deleteQRCode, getQRCodes, updateQRCodeName } from "../data/qrCodes"
import "../styles/QRCodeGenerator.scss"
import { useStateContext } from "contexts/userContext"
import { FlexColumnContainer, FlexRowContainer } from "styles/styledComponents"
import {
  Button,
  Card,
  CardContent,
  CardActions,
  TextField,
  Box,
  Typography,
  IconButton,
  Chip,
  Alert,
  Paper,
  Grid,
  Tooltip,
  Container,
} from "@mui/material"
import DeleteIcon from "@mui/icons-material/Delete"
import PrintIcon from "@mui/icons-material/Print"
import SaveIcon from "@mui/icons-material/Save"
import EditIcon from "@mui/icons-material/Edit"
import CheckIcon from "@mui/icons-material/Check"
import CloseIcon from "@mui/icons-material/Close"
import LinkIcon from "@mui/icons-material/Link"
import QrCode2Icon from "@mui/icons-material/QrCode2"
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline"
import ImageIcon from "@mui/icons-material/Image"
import CodeIcon from "@mui/icons-material/Code"
import { useTranslation } from "react-i18next"

export const QRCodes = () => {
  const [qrCodes, setQRCodes] = useState([])
  const [inputValue, setInputValue] = useState("")
  const [error, setError] = useState(null)
  const [qrColor, setQRColor] = useState("#000000") // Состояние для цвета QR-кода
  const [errorCorrectionLevel, setErrorCorrectionLevel] = useState("L") // Состояние для уровня ошибок QR-кода
  const { restaurant_id } = useStateContext()
  const qrCodeRefs = useRef({})
  const [editMode, setEditMode] = useState(null) // для отслеживания, какой QR-код редактируется
  const [editedName, setEditedName] = useState("") // для хранения редактируемого имени
  const { t } = useTranslation()
  const [originalName, setOriginalName] = useState("") // для хранения оригинального имени

  const handleEditQRCodeName = (qr) => {
    console.log("Editing QR Code:", qr) // Логируем объект qr
    setEditedName(qr.code_name) // Устанавливаем текущее имя в поле ввода
    setOriginalName(qr.code_name) // Сохраняем оригинальное имя для отмены изменений
    setEditMode(qr.id) // Включаем режим редактирования
  }

  const handleCancelEdit = () => {
    setEditedName(originalName) // Восстанавливаем оригинальное имя
    setEditMode(null) // Выход из режима редактирования
  }

  const handleSaveEditedQRCodeName = async (qrCodeId) => {
    try {
      await updateQRCodeName(qrCodeId, editedName) // Обновляем имя QR-кода на сервере
      setQRCodes((prevCodes) => prevCodes.map((qr) => (qr.id === qrCodeId ? { ...qr, code_name: editedName } : qr)))
      setEditMode(null) // Выход из режима редактирования
    } catch (error) {
      setError(t("Failed to update QR code name. Please try again."))
    }
  }

  useEffect(() => {
    fetchQRCodes()
  }, [restaurant_id])

  const fetchQRCodes = async () => {
    try {
      const codes = await getQRCodes(restaurant_id)
      setQRCodes(codes)
      setError(null)
    } catch (error) {
      setError(t("Failed to fetch QR codes. Please try again."))
    }
  }

  const handleInputChange = (e) => {
    setInputValue(e.target.value)
  }

  const handleColorChange = (e) => {
    setQRColor(e.target.value)
  }

  const handleAddQRCode = async () => {
    if (inputValue.trim() !== "") {
      try {
        const site_url = "https://qrseller-client.netlify.app/"
        const qrCodeValue = `${site_url}?restaurant_id=${restaurant_id}&qr_name=${encodeURIComponent(inputValue)}`

        console.log(
          "restaurant_id, inputValue, qrCodeValue, qrColor :>> ",
          restaurant_id,
          inputValue,
          qrCodeValue,
          qrColor
        )
        await saveQRCode(restaurant_id, inputValue, qrCodeValue, qrColor)

        setQRCodes([
          ...qrCodes,
          {
            id: restaurant_id,
            code_name: inputValue,
            code_value: qrCodeValue,
            qr_color: qrColor,
          },
        ])

        setInputValue("")
        setError(null)
      } catch (error) {
        setError(t("Failed to add QR code. Please try again."))
      }
    }
  }

  const handleDeleteQRCode = async (codeId) => {
    try {
      await deleteQRCode(codeId)
      setQRCodes(qrCodes.filter((code) => code.id !== codeId))
      setError(null)
    } catch (error) {
      setError(t("Failed to delete QR code. Please try again."))
    }
  }

  const handlePrintQRCode = (qrCode) => {
    const printWindow = window.open("", "_blank")
    printWindow.document.write(`<html><head><title>${t("Print QR Code")}</title></head><body>`)
    printWindow.document.write("<h1>" + t(qrCode.code_name) + "</h1>")
    printWindow.document.write(qrCodeRefs.current[qrCode.id].innerHTML)
    printWindow.document.write("</body></html>")
    printWindow.document.close()
    printWindow.print()
  }

  const handleSaveQRCodeSVG = (qrCode) => {
    const svg = qrCodeRefs.current[qrCode.id].querySelector("svg")
    const svgData = new XMLSerializer().serializeToString(svg)

    const downloadLink = document.createElement("a")
    downloadLink.download = `${qrCode.code_name}_qr_code.svg`
    downloadLink.href = "data:image/svg+xml;base64," + btoa(svgData)
    downloadLink.click()
  }

  const handleSaveQRCode = (qrCode) => {
    const svg = qrCodeRefs.current[qrCode.id].querySelector("svg")
    const svgData = new XMLSerializer().serializeToString(svg)

    const canvas = document.createElement("canvas")
    const bbox = svg.getBBox()
    canvas.width = bbox.width * 8 // увеличиваем разрешение в 8 раз
    canvas.height = bbox.height * 8

    const img = new Image()
    img.onload = () => {
      const ctx = canvas.getContext("2d")
      ctx.imageSmoothingEnabled = true // включаем сглаживание
      ctx.imageSmoothingQuality = "high" // устанавливаем высокое качество сглаживания
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height)

      canvas.toBlob(
        (blob) => {
          const downloadLink = document.createElement("a")
          downloadLink.download = `${qrCode.code_name}_qr_code.png`
          downloadLink.href = URL.createObjectURL(blob)
          downloadLink.click()
        },
        "image/png",
        0.98 // устанавливаем очень высокое качество сжатия (98%)
      )
    }

    img.src = "data:image/svg+xml;base64," + btoa(svgData)
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
          <QrCode2Icon sx={{ fontSize: 40, color: "primary.main" }} />
          <Typography variant="h3" component="h1" sx={{ fontWeight: 600 }}>
            {t("Qr code generator")}
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {t("Create and manage QR codes for your restaurant tables and locations")}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Paper elevation={3} sx={{ p: 3, mb: 4, borderRadius: 2 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
            {t("Create New QR Code")}
          </Typography>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
            <TextField
              value={inputValue}
              onChange={handleInputChange}
              placeholder={t("Enter QR code name (e.g., table number or street)")}
              variant="outlined"
              sx={{ flex: 1, minWidth: "250px" }}
              InputProps={{
                startAdornment: <QrCode2Icon sx={{ mr: 1, color: "action.active" }} />,
              }}
            />

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Tooltip title={t("Choose QR code color")}>
                <Box
                  sx={{
                    position: "relative",
                    width: 56,
                    height: 56,
                    borderRadius: 2,
                    border: "2px solid",
                    borderColor: "divider",
                    overflow: "hidden",
                    cursor: "pointer",
                    transition: "transform 0.2s",
                    "&:hover": {
                      transform: "scale(1.05)",
                    },
                  }}
                >
                  <input
                    type="color"
                    id="colorPicker"
                    value={qrColor}
                    onChange={handleColorChange}
                    style={{
                      position: "absolute",
                      width: "100%",
                      height: "100%",
                      border: "none",
                      cursor: "pointer",
                    }}
                  />
                </Box>
              </Tooltip>

              <Button
                onClick={handleAddQRCode}
                variant="contained"
                size="large"
                startIcon={<AddCircleOutlineIcon />}
                sx={{
                  px: 3,
                  py: 1.5,
                  fontWeight: 600,
                  textTransform: "none",
                  fontSize: "1rem",
                }}
              >
                {t("Add")}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box>

      <Grid container spacing={3}>
        {qrCodes.map((qr) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={qr.id}>
            <Card
              elevation={2}
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                transition: "transform 0.2s, box-shadow 0.2s",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: 6,
                },
              }}
            >
              <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                {editMode === qr.id ? (
                  <Box sx={{ mb: 2 }}>
                    <TextField
                      fullWidth
                      size="small"
                      value={editedName}
                      onChange={(e) => setEditedName(e.target.value)}
                      sx={{ mb: 1 }}
                    />
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Button
                        size="small"
                        variant="contained"
                        color="success"
                        startIcon={<CheckIcon />}
                        onClick={() => handleSaveEditedQRCodeName(qr.id)}
                        sx={{ flex: 1 }}
                      >
                        {t("Save")}
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<CloseIcon />}
                        onClick={handleCancelEdit}
                        sx={{ flex: 1 }}
                      >
                        {t("Cancel")}
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: qr.qr_color }}>
                      {t(qr.code_name)}
                    </Typography>
                    <IconButton size="small" onClick={() => handleEditQRCodeName(qr)} color="primary">
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}

                <Paper
                  elevation={0}
                  ref={(el) => (qrCodeRefs.current[qr.id] = el)}
                  sx={{
                    p: 2,
                    mb: 2,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    backgroundColor: "grey.50",
                    borderRadius: 2,
                  }}
                >
                  <QRCode
                    value={qr.code_value}
                    renderAs="svg"
                    size={256}
                    fgColor={qr.qr_color || "#000000"}
                    level={errorCorrectionLevel}
                    style={{ width: "100%", height: "auto", maxWidth: "200px" }}
                  />
                </Paper>

                <Chip
                  icon={<LinkIcon />}
                  label={t("View Link")}
                  onClick={() => window.open(qr.code_value, "_blank")}
                  size="small"
                  sx={{ width: "100%", mb: 1 }}
                  color="primary"
                  variant="outlined"
                />
              </CardContent>

              <CardActions sx={{ flexDirection: "column", gap: 0.5, p: 2, pt: 0 }}>
                <Button
                  fullWidth
                  variant="outlined"
                  size="small"
                  startIcon={<PrintIcon />}
                  onClick={() => handlePrintQRCode(qr)}
                  color="success"
                >
                  {t("Print")}
                </Button>

                <Button
                  fullWidth
                  variant="outlined"
                  size="small"
                  startIcon={<ImageIcon />}
                  onClick={() => handleSaveQRCode(qr)}
                  color="info"
                >
                  {t("Save as Image")}
                </Button>

                <Button
                  fullWidth
                  variant="outlined"
                  size="small"
                  startIcon={<CodeIcon />}
                  onClick={() => handleSaveQRCodeSVG(qr)}
                  color="info"
                >
                  {t("Save as Vector")}
                </Button>

                <Button
                  fullWidth
                  variant="outlined"
                  size="small"
                  startIcon={<DeleteIcon />}
                  onClick={() => handleDeleteQRCode(qr.id)}
                  color="error"
                  sx={{ mt: 1 }}
                >
                  {t("Delete")}
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {qrCodes.length === 0 && !error && (
        <Paper
          elevation={0}
          sx={{
            p: 6,
            textAlign: "center",
            backgroundColor: "grey.50",
            borderRadius: 2,
          }}
        >
          <QrCode2Icon sx={{ fontSize: 80, color: "action.disabled", mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {t("No QR codes yet")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("Create your first QR code to get started")}
          </Typography>
        </Paper>
      )}
    </Container>
  )
}
